import { generatePath, matchPath } from 'react-router-dom';
import { recordNavigationTelemetry } from './telemetry';
import type {
  RouteDescriptor,
  RouteGuardId,
  RoutePolicy,
  RouterBasename,
  ShellId,
} from './types';

const MAIN = ['/', '/app'] as const;
const MANAGER = ['/manager'] as const;
const HR = ['/hr'] as const;
const ACCOUNTANT = ['/accountant'] as const;

type PolicyOptions = {
  routePath?: string;
  basenames?: readonly RouterBasename[];
  instance?: RoutePolicy['instance'];
  aliases?: readonly string[];
};

const p = (
  id: string,
  routerPath: string,
  shell: ShellId,
  guard: RouteGuardId,
  fallbackRouterPath: string,
  options: PolicyOptions = {},
): RoutePolicy => ({
  id,
  routerPath,
  routePath: options.routePath ?? routerPath,
  basenames: options.basenames ?? MAIN,
  shell,
  guard,
  fallbackRouterPath,
  aliases: options.aliases,
  instance: options.instance ?? 'route',
  activity: 'reconstruct',
});

const d = (routerPath: string, routePath = routerPath, canonicalTo?: string): RouteDescriptor => ({
  routerPath,
  routePath,
  canonicalTo,
  declarationOnly: true,
});

export const ROUTES = {
  // Public site, auth, diagnostics, and error surfaces.
  'public.home': p('public.home', '/', 'public', 'public', '/'),
  'public.jobApplications': p('public.job-applications', '/job-applications', 'public', 'public', '/'),
  'public.about': p('public.about', '/about', 'public', 'public', '/'),
  'public.contact': p('public.contact', '/contact', 'public', 'public', '/'),
  'public.courseCatalog': p('public.course-catalog', '/course', 'public', 'public', '/'),
  'public.courseDetail': p('public.course-detail', '/course/:slug', 'public', 'public', '/course', { instance: 'pathname' }),
  'public.programs': p('public.programs', '/programs', 'public', 'public', '/'),
  'public.eventCertificate': p('public.event-certificate', '/event-certificate', 'public', 'public', '/'),
  'public.placementTest': p('public.placement-test', '/placement-test', 'public', 'public', '/'),
  'public.privacy': p('public.privacy', '/privacy', 'public', 'public', '/'),
  'public.terms': p('public.terms', '/termsofservice', 'public', 'public', '/'),
  'auth.login': p('auth.login', '/login', 'auth', 'public', '/', { aliases: ['/app-shell', '/app-shell.html'] }),
  'auth.register': p('auth.register', '/register', 'auth', 'public', '/login'),
  'auth.forgotPassword': p('auth.forgot-password', '/forgot-password', 'auth', 'public', '/login'),
  'auth.resetPassword': p('auth.reset-password', '/reset-password', 'auth', 'public', '/login'),
  'auth.setPassword': p('auth.set-password', '/set-password', 'auth', 'public', '/login'),
  'auth.googleFollowup': p('auth.google-followup', '/auth/google-followup', 'auth', 'public', '/login'),
  'receipt.verify': p('receipt.verify', '/receipt/verify/:token', 'public', 'public', '/', { instance: 'pathname' }),
  'diagnostics.health': p('diagnostics.health', '/health-dashboard', 'diagnostics', 'public', '/'),
  'diagnostics.dashboard': p('diagnostics.dashboard', '/diagnostics/dashboard', 'diagnostics', 'public', '/'),
  'diagnostics.wsHealth': p('diagnostics.ws-health', '/diagnostics/ws-health', 'diagnostics', 'public', '/'),
  'diagnostics.bundleHealth': p('diagnostics.bundle-health', '/diagnostics/bundle/health', 'diagnostics', 'public', '/'),
  'diagnostics.livePrebuilt': p('diagnostics.live-prebuilt', '/live/prebuilt', 'diagnostics', 'public', '/'),
  'error.404': p('error.404', '/error/404', 'error', 'public', '/'),
  'error.403': p('error.403', '/error/403', 'error', 'public', '/'),
  'error.401': p('error.401', '/error/401', 'error', 'public', '/'),
  'error.400': p('error.400', '/error/400', 'error', 'public', '/'),
  'error.500': p('error.500', '/error/500', 'error', 'public', '/'),
  'error.503': p('error.503', '/error/503', 'error', 'public', '/'),
  'error.network': p('error.network', '/error/network', 'error', 'public', '/'),

  // Employee auth and protected portal.
  'employee.settings': p('employee.settings', '/settings', 'employee', 'staff', '/', { basenames: ['/', '/app', '/manager', '/hr', '/accountant'] }),
  'employee.login': p('employee.login', '/employee/login', 'auth', 'public', '/employee/login'),
  'employee.register': p('employee.register', '/employee/register', 'auth', 'public', '/employee/login'),
  'employee.forgotPassword': p('employee.forgot-password', '/employee/forgot-password', 'auth', 'public', '/employee/login'),
  'employee.resetPassword': p('employee.reset-password', '/employee/reset-password', 'auth', 'public', '/employee/login'),
  'employee.googleFollowup': p('employee.google-followup', '/employee/auth/google-followup', 'auth', 'public', '/employee/login'),
  'employee.profile': p('employee.profile', '/employee/profile', 'employee', 'authenticated', '/employee/profile', { aliases: ['/employee'] }),
  'employee.messages': p('employee.messages', '/employee/messages', 'employee', 'authenticated', '/employee/profile'),
  'employee.mail': p('employee.mail', '/employee/mail', 'employee', 'authenticated', '/employee/profile'),
  'employee.requests': p('employee.requests', '/employee/requests', 'employee', 'authenticated', '/employee/profile'),
  'employee.instructorPayroll': p('employee.instructor-payroll', '/employee/instructor-payroll', 'employee', 'authenticated', '/employee/profile'),
  'employee.attendance': p('employee.attendance', '/employee/attendance', 'employee', 'authenticated', '/employee/profile'),

  // Reception lists and workspaces.
  'reception.home': p('reception.home', '/reception', 'reception', 'reception', '/reception'),
  'reception.students': p('reception.students', '/reception/students', 'reception', 'reception', '/reception', { aliases: ['/reception/waiting'] }),
  'reception.oldSystem': p('reception.old-system', '/reception/old-system', 'reception', 'reception', '/reception'),
  'reception.oldSystemStudent': p('reception.old-system-student', '/reception/old-system/:sourceId/:legacyId', 'reception', 'reception', '/reception/old-system', { instance: 'pathname' }),
  'reception.waitingInsights': p('reception.waiting-insights', '/reception/waiting-insights', 'reception', 'reception', '/reception/students'),
  'reception.groups': p('reception.groups', '/reception/groups', 'reception', 'reception', '/reception'),
  'reception.quarter': p('reception.quarter', '/reception/quarter', 'reception', 'reception', '/reception'),
  'reception.calendar': p('reception.calendar', '/reception/calendar', 'reception', 'reception', '/reception'),
  'reception.finance': p('reception.finance', '/reception/finance', 'reception', 'reception-finance', '/reception'),
  'reception.branchFinance': p('reception.branch-finance', '/reception/branch-finance', 'reception', 'reception', '/reception'),
  'reception.salesInvoices': p('reception.sales-invoices', '/reception/sales-invoices', 'reception', 'reception', '/reception'),
  'reception.certificates': p('reception.certificates', '/reception/certificates', 'reception', 'reception', '/reception'),
  'reception.eventCertificates': p('reception.event-certificates', '/reception/event-certificates', 'reception', 'reception', '/reception'),
  'reception.mail': p('reception.mail', '/reception/mail', 'mail', 'reception', '/reception'),
  'reception.proFinance': p('reception.pro-finance', '/reception/pro-finance', 'reception', 'reception-finance', '/reception'),
  'reception.placementPayments': p('reception.placement-payments', '/reception/placement-payments', 'reception', 'reception', '/reception'),
  'reception.refunds': p('reception.refunds', '/reception/refunds', 'reception', 'reception', '/reception'),
  'reception.requests': p('reception.requests', '/reception/requests', 'reception', 'reception', '/reception'),
  'reception.courseInterests': p('reception.course-interests', '/reception/course-interests', 'reception', 'reception', '/reception'),
  'reception.consultSlots': p('reception.consult-slots', '/reception/sales-consult-slots', 'reception', 'reception', '/reception'),
  'reception.consultQueue': p('reception.consult-queue', '/reception/sales-consult-queue', 'reception', 'reception', '/reception'),
  'reception.tasks': p('reception.tasks', '/reception/tasks', 'reception', 'reception', '/reception'),
  'reception.tasksManage': p('reception.tasks-manage', '/reception/tasks-manage', 'reception', 'reception-super', '/reception/tasks'),
  'reception.dailyReports': p('reception.daily-reports', '/reception/daily-reports', 'reception', 'reception-super', '/reception'),
  'reception.groupReports': p('reception.group-reports', '/reception/reports', 'reception', 'reception-super', '/reception'),
  'reception.branchSummary': p('reception.branch-summary', '/reception/reports/summary', 'reception', 'reception-super', '/reception'),
  'reception.groupBalances': p('reception.group-balances', '/reception/reports/group-balances', 'reception', 'reception-super', '/reception'),
  'reception.bookingReports': p('reception.booking-reports', '/reception/reports/bookings', 'reception', 'reception-super', '/reception'),
  'reception.chat': p('reception.chat', '/reception/chat', 'reception', 'reception', '/reception'),
  'reception.superLogs': p('reception.super-logs', '/reception/super/logs', 'reception', 'reception-super', '/reception'),
  'reception.studentRegister': p('reception.student-register', '/reception/students/register', 'reception', 'reception', '/reception/students'),
  'reception.studentInvoice': p('reception.student-invoice', '/reception/students/:id/invoice', 'reception', 'reception', '/reception/students', { instance: 'pathname' }),
  'reception.invoiceReceipt': p('reception.invoice-receipt', '/reception/invoices/:invoiceId', 'reception', 'reception', '/reception/students', { instance: 'pathname' }),
  'reception.summerReceipt': p('reception.summer-receipt', '/reception/summer-training/receipts/:receiptId', 'reception', 'reception', '/reception/finance', { instance: 'pathname' }),
  'reception.examNew': p('reception.exam-new', '/reception/exams/new', 'reception', 'reception-language', '/reception/exams'),
  'reception.examEdit': p('reception.exam-edit', '/reception/exams/:id/edit', 'reception', 'reception-language', '/reception/exams', { instance: 'pathname' }),
  'reception.examAudit': p('reception.exam-audit', '/reception/exams/:id/audit', 'reception', 'reception-language', '/reception/exams', { instance: 'pathname' }),
  'reception.examView': p('reception.exam-view', '/reception/exams/:id/view', 'reception', 'reception-language', '/reception/exams', { instance: 'pathname' }),
  'reception.exams': p('reception.exams', '/reception/exams', 'reception', 'reception-language', '/reception/language'),
  'reception.attempt': p('reception.attempt', '/reception/attempts/:id', 'reception', 'reception-language', '/reception/exams', { instance: 'pathname' }),
  'reception.attendanceMatrixGroup': p('reception.attendance-matrix-group', '/reception/attendance/matrix/:groupId', 'reception', 'reception', '/reception/attendance/matrix', { instance: 'pathname' }),
  'reception.attendanceMatrix': p('reception.attendance-matrix', '/reception/attendance/matrix', 'reception', 'reception', '/reception/groups'),
  'reception.groupAttendance': p('reception.group-attendance', '/reception/attendance/group/:groupId', 'reception', 'reception', '/reception/groups', { instance: 'pathname' }),
  'reception.sessionAttendance': p('reception.session-attendance', '/reception/attendance/session/:sessionId', 'reception', 'reception', '/reception/attendance/matrix', { instance: 'pathname' }),
  'reception.language': p('reception.language', '/reception/language', 'reception', 'reception-language', '/reception'),
  'reception.languageImport': p('reception.language-import', '/reception/language/exams/import', 'reception', 'reception-language', '/reception/language'),
  'reception.languageImportProgress': p('reception.language-import-progress', '/reception/language/exams/import/progress/:jobId', 'reception', 'reception-language', '/reception/language/exams/import', { instance: 'pathname' }),
  'reception.languageMaterials': p('reception.language-materials', '/reception/language/materials', 'reception', 'reception-language', '/reception/language', { aliases: ['/reception/materials'] }),
  'reception.inactiveStudents': p('reception.inactive-students', '/reception/language/students/inactive', 'reception', 'reception-language', '/reception/language'),

  // Manager basename.
  'manager.dashboard': p('manager.dashboard', '/', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.employees': p('manager.employees', '/employees', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.tasks': p('manager.tasks', '/tasks', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.mail': p('manager.mail', '/mail', 'mail', 'manager', '/', { basenames: MANAGER }),
  'manager.branchTransfers': p('manager.branch-transfers', '/branch-transfers', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.groups': p('manager.groups', '/groups', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.groupAttendance': p('manager.group-attendance', '/attendance/group/:groupId', 'manager', 'manager', '/groups', { basenames: MANAGER, instance: 'pathname' }),
  'manager.sessionAttendance': p('manager.session-attendance', '/attendance/session/:sessionId', 'manager', 'manager', '/groups', { basenames: MANAGER, instance: 'pathname' }),
  'manager.quarter': p('manager.quarter', '/quarter', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.students': p('manager.students', '/students', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.studentInvoice': p('manager.student-invoice', '/students/:id/invoice', 'manager', 'manager', '/students', { basenames: MANAGER, instance: 'pathname' }),
  'manager.invoiceReceipt': p('manager.invoice-receipt', '/invoices/:invoiceId', 'manager', 'manager', '/students', { basenames: MANAGER, instance: 'pathname' }),
  'manager.calendar': p('manager.calendar', '/calendar', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.superLogs': p('manager.super-logs', '/super/logs', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.instructorPayroll': p('manager.instructor-payroll', '/instructor-payroll', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.finance': p('manager.finance', '/finance', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.proFinance': p('manager.pro-finance', '/pro-finance', 'manager', 'manager', '/', { basenames: MANAGER }),
  'manager.importLeads': p('manager.import-leads', '/import-leads', 'manager', 'manager', '/', { basenames: MANAGER }),

  // HR basename.
  'hr.home': p('hr.home', '/', 'hr', 'hr', '/', { basenames: HR }),
  'hr.dashboard': p('hr.dashboard', '/dashboard', 'hr', 'hr', '/', { basenames: HR }),
  'hr.employees': p('hr.employees', '/employees', 'hr', 'hr', '/', { basenames: HR }),
  'hr.schedules': p('hr.schedules', '/schedules', 'hr', 'hr', '/', { basenames: HR }),
  'hr.messages': p('hr.messages', '/messages', 'hr', 'hr', '/', { basenames: HR }),
  'hr.mail': p('hr.mail', '/mail', 'mail', 'hr', '/', { basenames: HR }),
  'hr.requests': p('hr.requests', '/requests', 'hr', 'hr', '/', { basenames: HR }),
  'hr.payroll': p('hr.payroll', '/payroll', 'hr', 'hr', '/', { basenames: HR }),
  'hr.instructorPayroll': p('hr.instructor-payroll', '/instructor-payroll', 'hr', 'hr', '/', { basenames: HR }),
  'hr.groupRates': p('hr.group-rates', '/group-rates', 'hr', 'hr', '/', { basenames: HR }),
  'hr.attendance': p('hr.attendance', '/attendance', 'hr', 'hr', '/', { basenames: HR }),
  'hr.reports': p('hr.reports', '/reports', 'hr', 'hr', '/', { basenames: HR }),

  // Accountant basename; child routePath values remain relative to AccountantShell.
  'accountant.home': p('accountant.home', '/', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT }),
  'accountant.dashboard': p('accountant.dashboard', '/dashboard', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'dashboard' }),
  'accountant.income': p('accountant.income', '/income', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'income' }),
  'accountant.salesCollections': p('accountant.sales-collections', '/sales-collections', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'sales-collections' }),
  'accountant.expenses': p('accountant.expenses', '/expenses', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'expenses' }),
  'accountant.refunds': p('accountant.refunds', '/refunds', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'refunds' }),
  'accountant.estimates': p('accountant.customer-estimates', '/customer-estimates', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'customer-estimates' }),
  'accountant.reports': p('accountant.reports', '/reports', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'reports' }),
  'accountant.cashFlow': p('accountant.cash-flow', '/cash-flow', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'cash-flow' }),
  'accountant.installments': p('accountant.installments', '/installments', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'installments' }),
  'accountant.instructorPayroll': p('accountant.instructor-payroll', '/instructor-payroll', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'instructor-payroll' }),
  'accountant.groupRates': p('accountant.group-rates', '/group-rates', 'accountant', 'accountant', '/', { basenames: ACCOUNTANT, routePath: 'group-rates' }),

  // Sales, Marketing, shared Mail, and Developer.
  'sales.home': p('sales.home', '/sales', 'sales', 'sales', '/sales', { routePath: '/' }),
  'sales.tab': p('sales.tab', '/sales/:tabKey', 'sales', 'sales', '/sales', { routePath: ':tabKey', instance: 'pathname' }),
  // Workboards admits eligible department employees through its server session,
  // independently of access to the legacy Marketing reports.
  'marketing.work': p('marketing.work', '/marketing/work/*', 'marketing', 'staff', '/marketing/work/boards', { instance: 'pathname' }),
  'marketing.workBoards': p('marketing.work-boards', '/marketing/work/boards', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.workBoard': p('marketing.work-board', '/marketing/work/boards/:boardId', 'marketing', 'staff', '/marketing/work/boards', { instance: 'pathname' }),
  'marketing.workMy': p('marketing.work-my', '/marketing/work/my', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.workSearch': p('marketing.work-search', '/marketing/work/search', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.workInbox': p('marketing.work-inbox', '/marketing/work/inbox', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.workPlanner': p('marketing.work-planner', '/marketing/work/planner', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.workNotifications': p('marketing.work-notifications', '/marketing/work/notifications', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.workDepartment': p('marketing.work-department', '/marketing/work/department', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.workSettings': p('marketing.work-settings', '/marketing/work/settings', 'marketing', 'staff', '/marketing/work/boards'),
  'marketing.moderator': p('marketing.moderator', '/marketing/moderator', 'marketing', 'marketing', '/marketing/moderator', { routePath: 'moderator' }),
  'marketing.moderatorFollowUps': p('marketing.moderator-follow-ups', '/marketing/moderator/follow-ups', 'marketing', 'marketing', '/marketing/moderator', { routePath: 'moderator/follow-ups' }),
  'marketing.moderatorResults': p('marketing.moderator-results', '/marketing/moderator/results', 'marketing', 'marketing', '/marketing/moderator', { routePath: 'moderator/results' }),
  'marketing.dashboard': p('marketing.dashboard', '/marketing/dashboard', 'marketing', 'marketing', '/marketing/dashboard', { routePath: 'dashboard', aliases: ['/marketing'] }),
  'marketing.courses': p('marketing.courses', '/marketing/courses', 'marketing', 'marketing', '/marketing/dashboard', { routePath: 'courses' }),
  'marketing.catalog': p('marketing.catalog', '/marketing/catalog', 'marketing', 'marketing', '/marketing/dashboard', { routePath: 'catalog' }),
  'marketing.jobApplications': p('marketing.job-applications', '/marketing/job-applications', 'marketing', 'marketing', '/marketing/dashboard', { routePath: 'job-applications' }),
  'marketing.contactInquiries': p('marketing.contact-inquiries', '/marketing/contact-inquiries', 'marketing', 'marketing', '/marketing/dashboard', { routePath: 'contact-inquiries' }),
  'marketing.internshipRegistrations': p('marketing.internship-registrations', '/marketing/internship-registrations', 'marketing', 'marketing', '/marketing/dashboard', { routePath: 'internship-registrations' }),
  'marketing.mail': p('marketing.mail', '/marketing/mail', 'mail', 'marketing', '/marketing/dashboard', { routePath: 'mail' }),
  'marketing.salesInvoices': p('marketing.sales-invoices', '/marketing/sales-invoices', 'marketing', 'marketing', '/marketing/dashboard', { routePath: 'sales-invoices' }),
  'mail.home': p('mail.home', '/mail', 'mail', 'authenticated', '/'),
  'developer.cockpit': p('developer.cockpit', '/developer/cockpit', 'developer', 'developer', '/developer/cockpit'),
  'developer.errors': p('developer.errors', '/developer/errors', 'developer', 'developer', '/developer/cockpit'),
  'developer.sandbox': p('developer.sandbox', '/developer/sandbox', 'developer', 'developer', '/developer/cockpit'),

  // Student and instructor portals.
  'student.dashboard': p('student.dashboard', '/student/dashboard', 'student', 'student', '/student/dashboard'),
  'student.profile': p('student.profile', '/student/profile', 'student', 'student', '/student/dashboard'),
  'student.languageProfile': p('student.language-profile', '/languages/student/profile', 'student', 'student', '/student/dashboard'),
  'student.group': p('student.group', '/student/groups/:id', 'student', 'student', '/student/dashboard', { instance: 'pathname' }),
  'student.languageExams': p('student.language-exams', '/languages/student/exams', 'student', 'student', '/student/dashboard'),
  'student.languageExamStart': p('student.language-exam-start', '/languages/student/exam/:examId/start', 'student', 'student', '/languages/student/exams', { instance: 'pathname' }),
  'student.languageAttempt': p('student.language-attempt', '/languages/student/exam/attempt/:attemptId', 'student', 'student', '/languages/student/exams', { instance: 'pathname' }),
  'student.examStart': p('student.exam-start', '/student/exams/:examId/start', 'student', 'student', '/student/dashboard', { instance: 'pathname' }),
  'student.liveViewer': p('student.live-viewer', '/student/live/viewer', 'student', 'student', '/student/dashboard'),
  'student.invoice': p('student.invoice', '/student/invoice/:invoiceId', 'student', 'student', '/student/profile', { instance: 'pathname' }),
  'instructor.home': p('instructor.home', '/instructor', 'instructor', 'instructor', '/instructor'),
  'instructor.profile': p('instructor.profile', '/instructor/profile', 'instructor', 'instructor', '/instructor'),
  'instructor.payroll': p('instructor.payroll', '/instructor/payroll', 'instructor', 'instructor', '/instructor'),
  'instructor.group': p('instructor.group', '/instructor/groups/:id', 'instructor', 'instructor', '/instructor', { instance: 'pathname' }),
  'instructor.live': p('instructor.live', '/instructor/groups/:id/live', 'instructor', 'instructor', '/instructor', { instance: 'pathname' }),
  'instructor.attempt': p('instructor.attempt', '/instructor/attempts/:attemptId', 'instructor', 'instructor', '/instructor', { instance: 'pathname' }),
  'instructor.attendance': p('instructor.attendance', '/instructor/attendance/session/:sessionId', 'instructor', 'instructor', '/instructor', { instance: 'pathname' }),

  // Placement flow. Token/exam routes remain reconstruct-only and never become origins.
  'placement.home': p('placement.home', '/placement', 'placement', 'public', '/placement'),
  'placement.continue': p('placement.continue', '/placement/continue/:token', 'placement', 'public', '/placement', { instance: 'pathname' }),
  'placement.exam': p('placement.exam', '/placement/exam', 'placement', 'public', '/placement'),
  'placement.status': p('placement.status', '/placement/status', 'placement', 'public', '/placement', { aliases: ['/placement/results'] }),
  'placement.linkExpired': p('placement.link-expired', '/placement/link-expired', 'placement', 'public', '/placement'),

  // Summer Training outer and inner router paths.
  'summer.home': p('summer.home', '/summer-training', 'summer', 'public', '/summer-training'),
  'summer.login': p('summer.login', '/summer-training/login', 'summer', 'public', '/summer-training', { routePath: 'login' }),
  'summer.studentRegister': p('summer.student-register', '/summer-training/student/register', 'summer', 'public', '/summer-training', { routePath: 'student/register', aliases: ['/summer-internship-2026'] }),
  'summer.studentDashboard': p('summer.student-dashboard', '/summer-training/student/dashboard', 'summer', 'summer-student', '/summer-training', { routePath: 'student/dashboard' }),
  'summer.studentPayment': p('summer.student-payment', '/summer-training/student/payment', 'summer', 'summer-student', '/summer-training/student/dashboard', { routePath: 'student/payment' }),
  'summer.studentGroups': p('summer.student-groups', '/summer-training/student/groups', 'summer', 'summer-student', '/summer-training/student/dashboard', { routePath: 'student/groups' }),
  'summer.studentTraining': p('summer.student-training', '/summer-training/student/training', 'summer', 'summer-student', '/summer-training/student/dashboard', { routePath: 'student/training' }),
  'summer.studentAttendance': p('summer.student-attendance', '/summer-training/student/attendance', 'summer', 'summer-student', '/summer-training/student/dashboard', { routePath: 'student/attendance' }),
  'summer.studentMaterials': p('summer.student-materials', '/summer-training/student/materials', 'summer', 'summer-student', '/summer-training/student/dashboard', { routePath: 'student/materials' }),
  'summer.studentTasks': p('summer.student-tasks', '/summer-training/student/tasks', 'summer', 'summer-student', '/summer-training/student/dashboard', { routePath: 'student/tasks' }),
  'summer.studentMore': p('summer.student-more', '/summer-training/student/more', 'summer', 'summer-student', '/summer-training/student/dashboard', { routePath: 'student/more' }),
  'summer.studentReceipt': p('summer.student-receipt', '/summer-training/student/receipt', 'summer', 'summer-student', '/summer-training/student/payment', { routePath: 'student/receipt' }),
  'summer.operations': p('summer.operations', '/summer-training/operations', 'summer', 'summer-staff', '/summer-training', { routePath: 'operations', aliases: ['/reception/summer-training'] }),
  'summer.finance': p('summer.finance', '/summer-training/finance', 'summer', 'summer-staff', '/summer-training', { routePath: 'finance' }),
  'summer.mentor': p('summer.mentor', '/summer-training/mentor', 'summer', 'summer-staff', '/summer-training', { routePath: 'mentor' }),
  'summer.lecturer': p('summer.lecturer', '/summer-training/lecturer', 'summer', 'summer-staff', '/summer-training', { routePath: 'lecturer' }),
  'summer.admin': p('summer.admin', '/summer-training/admin', 'summer', 'summer-staff', '/summer-training', { routePath: 'admin' }),
  'summer.createPassword': p('summer.create-password', '/summer-training/create-password', 'summer', 'public', '/summer-training/login', { routePath: 'create-password' }),
  'summer.resetPassword': p('summer.reset-password', '/summer-training/reset-password', 'summer', 'public', '/summer-training/login', { routePath: 'reset-password' }),
  'summer.reports': p('summer.reports', '/summer-training/reports', 'summer', 'summer-staff', '/summer-training', { routePath: 'reports' }),

  // Existing redirect aliases: accepted for canonical replacement, never as Safe Back origins.
  'alias.appShell': d('/app-shell', '/app-shell', '/login'),
  'alias.appShellHtml': d('/app-shell.html', '/app-shell.html', '/login'),
  'alias.employeeHome': d('/employee', '/employee', '/employee/profile'),
  'alias.receptionWaiting': d('/reception/waiting', '/reception/waiting', '/reception/students?category=waiting'),
  'alias.receptionMaterials': d('/reception/materials', '/reception/materials', '/reception/language/materials'),
  'alias.receptionSummer': d('/reception/summer-training', '/reception/summer-training', '/summer-training/operations'),
  'alias.placementResults': d('/placement/results', '/placement/results', '/placement/status'),
  'alias.summerInternship': d('/summer-internship-2026', '/summer-internship-2026', '/summer-training/student/register'),
  'alias.marketingHome': d('/marketing', '/marketing', '/marketing/dashboard'),

  // Mounts and wildcards used only by route declarations.
  'main.notFound': d('*'),
  'employee.authMount': d('/employee/*'),
  'employee.authNotFound': d('*'),
  'manager.notFound': d('*'),
  'hr.notFound': d('*'),
  'accountant.notFound': d('*'),
  'sales.mount': d('/sales/*'),
  'sales.notFound': d('/sales/*', '*'),
  'marketing.notFound': d('/marketing/*', '*'),
  'summer.mount': d('/summer-training/*'),
  'summer.notFound': d('/summer-training/*', '*'),
} as const satisfies Record<string, RouteDescriptor>;

export const SALES_TAB_KEYS = [
  'manager_dashboard',
  'course-assignments',
  'performance',
  'today',
  'pipeline',
  'tasks',
  'history',
  'mail',
  'lead_movement',
  'tele-sales-management',
  'facebook_mappings',
  'targets',
  'reports',
  'settings',
  'tele-sales',
] as const;

const SALES_TAB_KEY_SET = new Set<string>(SALES_TAB_KEYS);

const pathSegments = (path: string): string[] => path.split('/').filter(Boolean);
const splatCount = (path: string): number => pathSegments(path).filter((segment) => segment === '*').length;
const dynamicCount = (path: string): number => pathSegments(path).filter((segment) => segment.startsWith(':')).length;
const staticCount = (path: string): number => pathSegments(path).filter((segment) => segment !== '*' && !segment.startsWith(':')).length;

const compareRouteSpecificity = (left: RoutePolicy, right: RoutePolicy): number => {
  const basenameOrder = left.basenames.join('|').localeCompare(right.basenames.join('|'));
  if (basenameOrder !== 0) return basenameOrder;
  const splatOrder = splatCount(left.routerPath) - splatCount(right.routerPath);
  if (splatOrder !== 0) return splatOrder;
  const staticOrder = staticCount(right.routerPath) - staticCount(left.routerPath);
  if (staticOrder !== 0) return staticOrder;
  const dynamicOrder = dynamicCount(left.routerPath) - dynamicCount(right.routerPath);
  if (dynamicOrder !== 0) return dynamicOrder;
  return right.routerPath.length - left.routerPath.length;
};

const isRoutePolicy = (value: RouteDescriptor): value is RoutePolicy =>
  'id' in value && value.declarationOnly !== true;

export const ROUTE_POLICIES = Object.values(ROUTES)
  .filter(isRoutePolicy)
  .sort(compareRouteSpecificity);

const splitPathSuffix = (value: string): { pathname: string; suffix: string } => {
  const suffixIndex = value.search(/[?#]/);
  if (suffixIndex === -1) return { pathname: value, suffix: '' };
  return { pathname: value.slice(0, suffixIndex), suffix: value.slice(suffixIndex) };
};

const withLeadingSlash = (pathname: string): string => {
  if (!pathname) return '/';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
};

export function resolveAppBasename(pathname: string): RouterBasename {
  const { pathname: pathOnly } = splitPathSuffix(withLeadingSlash(pathname));
  for (const basename of ['/manager', '/hr', '/accountant', '/app'] as const) {
    if (pathOnly === basename || pathOnly.startsWith(`${basename}/`)) return basename;
  }
  return '/';
}

export function toDocumentPath(basename: RouterBasename, routerPath: string): string {
  const { pathname, suffix } = splitPathSuffix(routerPath);
  const normalizedPathname = withLeadingSlash(pathname);
  if (basename === '/') return `${normalizedPathname}${suffix}`;
  if (normalizedPathname === '/') return `${basename}${suffix}`;
  return `${basename}${normalizedPathname}${suffix}`;
}

export function toRouterPath(basename: RouterBasename, documentPath: string): string {
  const { pathname, suffix } = splitPathSuffix(documentPath);
  const normalizedPathname = withLeadingSlash(pathname);
  if (basename === '/') return `${normalizedPathname}${suffix}`;
  if (normalizedPathname === basename) return `/${suffix}`;
  if (normalizedPathname.startsWith(`${basename}/`)) {
    return `${normalizedPathname.slice(basename.length)}${suffix}`;
  }
  return `${normalizedPathname}${suffix}`;
}

export function resolveRoutePolicy({
  basename,
  pathname,
}: {
  basename: RouterBasename;
  pathname: string;
}): RoutePolicy | undefined {
  const pathOnly = splitPathSuffix(withLeadingSlash(pathname)).pathname;
  for (const policy of ROUTE_POLICIES) {
    if (!policy.basenames.includes(basename)) continue;
    const match = matchPath({ path: policy.routerPath, end: true }, pathOnly);
    if (!match) continue;
    if (policy.id === 'sales.tab' && !SALES_TAB_KEY_SET.has(match.params.tabKey ?? '')) continue;
    return policy;
  }
  return undefined;
}

type CanonicalAlias = {
  id: string;
  canonicalTo: string;
};

const findCanonicalAlias = (
  basename: RouterBasename,
  routerPathname: string,
): { alias: CanonicalAlias; params: Record<string, string | undefined> } | undefined => {
  for (const [id, descriptor] of Object.entries(ROUTES)) {
    if (!descriptor.canonicalTo) continue;
    const match = matchPath({ path: descriptor.routerPath, end: true }, routerPathname);
    if (match) {
      return {
        alias: { id, canonicalTo: descriptor.canonicalTo },
        params: match.params,
      };
    }
  }

  for (const policy of ROUTE_POLICIES) {
    if (!policy.basenames.includes(basename)) continue;
    for (const aliasPattern of policy.aliases ?? []) {
      const match = matchPath({ path: aliasPattern, end: true }, routerPathname);
      if (match) {
        return {
          alias: { id: policy.id, canonicalTo: policy.routerPath },
          params: match.params,
        };
      }
    }
  }
  return undefined;
};

const mergeCanonicalLocation = (
  canonicalTo: string,
  sourceSearch: string,
  sourceHash: string,
  params: Record<string, string | undefined>,
): string => {
  const target = new URL(canonicalTo, 'https://academy.test');
  const canonicalPathname = generatePath(target.pathname, params);
  const ownedKeys = new Set(target.searchParams.keys());
  const mergedSearch = new URLSearchParams(target.searchParams);
  for (const [key, value] of new URLSearchParams(sourceSearch)) {
    if (!ownedKeys.has(key)) mergedSearch.append(key, value);
  }
  const search = mergedSearch.size > 0 ? `?${mergedSearch.toString()}` : '';
  const hash = target.hash || sourceHash;
  return `${canonicalPathname}${search}${hash}`;
};

export function canonicalizeDocumentLocation(value: string): string {
  const original = new URL(value, 'https://academy.test');
  const basename = resolveAppBasename(original.pathname);
  let routerLocation = toRouterPath(basename, `${original.pathname}${original.search}${original.hash}`);
  const visitedAliasIds = new Set<string>();

  while (true) {
    const current = new URL(routerLocation, 'https://academy.test');
    const match = findCanonicalAlias(basename, current.pathname);
    if (!match) return toDocumentPath(basename, routerLocation);

    if (visitedAliasIds.has(match.alias.id)) {
      recordNavigationTelemetry({
        name: 'canonicalization_loop',
        reason: 'alias-loop',
      });
      return value;
    }
    visitedAliasIds.add(match.alias.id);

    routerLocation = mergeCanonicalLocation(
      match.alias.canonicalTo,
      current.search,
      current.hash,
      match.params,
    );
  }
}
